import { useNavigate, useLocation } from "react-router-dom";
import { useContext, useEffect, useMemo, useState } from "react";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import {
  ArrowLeft,
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  CreditCard,
  RotateCcw,
  Save,
  Hash,
  Star,
} from "lucide-react";

const SupplierMainMas = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const [supplierCode, setSupplierCode] = useState("");
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [supplierType, setSupplierType] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [ranking, setRanking] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [remarks, setRemarks] = useState("");
  const [openingBalance, setOpeningBalance] = useState("");
  const [tin, setTIN] = useState("");
  const [gst, setGST] = useState("");
  const [pan, setPAN] = useState("");
  const [tallyHeader, setTallyHeader] = useState("");

  const [divStateVisible, setDivStateVisible] = useState(false);
  const [divDistrictVisible, setDivDistrictVisible] = useState(false);
  const [divCityVisible, setDivCityVisible] = useState(false);
  const [stateText, setStateText] = useState("");
  const [districtText, setDistrictText] = useState("");
  const [cityText, setCityText] = useState("");
  const [createdSupplierId, setCreatedSupplierId] = useState("");

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const api = useMemo(() => {
    return axios.create({
      baseURL: "https://nissi-india.com/api",
      headers: commonHeaders,
    });
  }, [commonHeaders]);

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 ${
    isDark
      ? "bg-gray-800/50 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const resetForm = () => {
    setSupplierName("");
    setSupplierType("");
    setAddress1("");
    setAddress2("");
    setSelectedCountry("");
    setSelectedState("");
    setSelectedDistrict("");
    setSelectedCity("");
    setStates([]);
    setDistricts([]);
    setCities([]);
    setZipcode("");
    setPhone("");
    setMobile("");
    setEmail("");
    setRemarks("");
    setOpeningBalance("");
    setTIN("");
    setGST("");
    setPAN("");
    setRanking("");
    setTallyHeader("");
    setStateText("");
    setDistrictText("");
    setCityText("");
    setDivStateVisible(false);
    setDivDistrictVisible(false);
    setDivCityVisible(false);
    setCreatedSupplierId("");
    generateSupplierCode();
  };

    const loadExistingSupplier = async (id) => {
    try {
      const res = await api.get("/getSupplierMainEditDetails", {
        params: { supplierid: id },
      });
      const data = res.data?.supplierDetails;
      if (!data) return;

      setSupplierCode(data.fld_suppliercode || "");
      setSupplierName(data.fld_suppliername || "");
      setAddress1(data.fld_Address1 || "");
      setAddress2(data.fld_Address2 || "");
      setSelectedCountry(String(data.fld_Countryid || ""));
      setSelectedState(String(data.fld_stateid || ""));
      setSelectedDistrict(String(data.fld_Districtid || ""));
      setSelectedCity(String(data.fld_Cityid || ""));
      setZipcode(data.fld_ZipCode || "");
      setPhone(data.fld_phoneno || "");
      setMobile(data.fld_Mobile || "");
      setEmail(data.fld_Email || "");
      setRemarks(data.fld_Remarks || "");
      setOpeningBalance(data.fld_opening_balance || "");
      setTIN(data.fld_TIN || "");
      setGST(data.fld_CST || "");
      setPAN(data.fld_PAN || "");
      setRanking(String(data.fld_ranking || ""));
      setSupplierType(data.fld_VendorType || "");
      setTallyHeader(data.fld_tallyheader || "");

      if (data.fld_Countryid) await fetchStates(data.fld_Countryid);
      if (data.fld_stateid) await fetchDistricts(data.fld_stateid);
      if (data.fld_Districtid) await fetchCities(data.fld_Districtid);
      // Tab lists load automatically via the existing effect that
      // watches [selectedTab, createdSupplierId].
    } catch (err) {
      console.error("Error restoring supplier:", err);
      toast.error("Could not reload supplier data");
    }
  };

  const generateSupplierCode = async () => {
    try {
      const res = await api.get("/generateSupplierCode");
      if (res.data?.supplierCode) {
        setSupplierCode(res.data.supplierCode);
      } else {
        toast.error("Failed to generate supplier code");
      }
    } catch (error) {
      toast.error("Error fetching supplier code");
      console.error("Supplier code error:", error);
    }
  };

  const fetchCountries = async () => {
    try {
      const res = await api.get("/bindCountry");
      if (res.data?.countryList && Array.isArray(res.data.countryList)) {
        setCountries(res.data.countryList);
      } else {
        toast.error("Invalid country response format");
      }
    } catch (err) {
      toast.error("Error fetching countries");
      console.error("Country fetch error:", err);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId) return;
    try {
      const res = await api.get("/stateDropdownChanged", {
        params: { countryid: countryId },
      });
      if (res.data?.stateList && Array.isArray(res.data.stateList)) {
        setStates(res.data.stateList);
      } else {
        toast.error("Invalid state response format");
      }
    } catch (err) {
      toast.error("Error fetching states");
      console.error("State fetch error:", err);
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId || stateId === "other") return;
    try {
      const res = await api.get("/distDropdownChanged", {
        params: { stateid: stateId },
      });
      if (res.data?.distList && Array.isArray(res.data.distList)) {
        setDistricts(res.data.distList);
      } else {
        toast.error("Invalid district response format");
      }
    } catch (err) {
      toast.error("Error fetching districts");
      console.error("District fetch error:", err);
    }
  };

  const fetchCities = async (distId) => {
    if (!distId || distId === "other") return;
    try {
      const res = await api.get("/cityDropdownChanged", {
        params: { distid: distId },
      });
      if (res.data?.cityList && Array.isArray(res.data.cityList)) {
        setCities(res.data.cityList);
      } else {
        toast.error("Invalid city response format");
      }
    } catch (err) {
      toast.error("Error fetching cities");
      console.error("City fetch error:", err);
    }
  };

  const getWorkingSupplierId = () => createdSupplierId || "";

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

       if (
      !supplierName ||
      !supplierType ||
      !address1 ||
      !selectedCountry ||
      !zipcode ||
      !mobile ||
      !tin
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    // FIX: guard in case a pasted value slipped past the onChange filter.
    if (!/^\d{10}$/.test(mobile)) {
      toast.error("Mobile number must be exactly 10 digits.");
      return;
    }

    if (!/^\d{6}$/.test(zipcode)) {
      toast.error("Zip Code must be exactly 6 digits.");
      return;
    }

    let finalStateId = selectedState;
    let finalDistrictId = selectedDistrict;
    let finalCityId = selectedCity;

    if (
      selectedState === "other" ||
      selectedDistrict === "other" ||
      selectedCity === "other"
    ) {
      try {
        const res = await api.post("/insertAddressCity", {
          countryid: selectedCountry,
          stateText,
          districtText,
          cityText,
          stateidDropdown: selectedState !== "other" ? selectedState : "0",
          districtidDropdown:
            selectedDistrict !== "other" ? selectedDistrict : "0",
          cityidDropdown: selectedCity !== "other" ? selectedCity : "0",
          divStateVisible: divStateVisible ? "none" : "inline",
          divDistrictVisible: divDistrictVisible ? "none" : "inline",
          divCityVisible: divCityVisible ? "none" : "inline",
        });

        finalStateId = res.data?.stateid || finalStateId;
        finalDistrictId = res.data?.districtid || finalDistrictId;
        finalCityId = res.data?.cityid || finalCityId;

        if (divStateVisible && stateText && finalStateId) {
          setStates((prev) => [
            ...prev,
            { fld_stateid: finalStateId, fld_statename: stateText },
          ]);
          setSelectedState(String(finalStateId));
        }

        if (divDistrictVisible && districtText && finalDistrictId) {
          setDistricts((prev) => [
            ...prev,
            {
              fld_districtid: finalDistrictId,
              fld_districtname: districtText,
            },
          ]);
          setSelectedDistrict(String(finalDistrictId));
        }

        if (divCityVisible && cityText && finalCityId) {
          setCities((prev) => [
            ...prev,
            { fld_cityid: finalCityId, fld_cityname: cityText },
          ]);
          setSelectedCity(String(finalCityId));
        }
      } catch (err) {
        console.error("Insert Address Error:", err);
        toast.error("Error inserting new address");
        return;
      }
    }

    const payload = {
      suppliername: supplierName,
      suppliercode: supplierCode,
      address1,
      address2,
      cityid: finalCityId || null,
      stateid: finalStateId || null,
      countryid: selectedCountry || null,
      districtid: finalDistrictId || null,
      zipcode,
      phoneno: phone,
      mobile,
      email,
      remarks,
      userid: userId || null,
      opening_balance: openingBalance || 0,
      ranking: ranking || 0,
      TIN: tin,
      CST: gst,
      PAN: pan,
      VendorType: supplierType,
    };

    try {
      const res = await api.post("/insertSupplier", payload);

            if (res.data?.supplierid) {
        setCreatedSupplierId(res.data.supplierid);
        toast.success("Supplier added successfully. You can add Statutory, Bank, and Contact details after editing this supplier.");
      } else if (res.data?.message) {
        toast.success(res.data.message);
      } else {
        toast.error("Submission failed");
      }
    } catch (err) {
      console.error("Insert Supplier Error:", err);
      toast.error(
        err?.response?.data?.error || "Error submitting supplier data"
      );
    }
  };








  // ✅ FIX: this page unmounts when you navigate to the Bank/Contact/
  // Statutory add screen. createdSupplierId is plain React state, so it's
  // lost on remount when you come back — the DB record is still there,
  // the page just forgets it was created and silently restarts as a
  // brand-new entry (fresh code, blank form, empty tab lists). If we're
  // returning with a supplierid passed back through navigation state,
  // restore it and reload that existing supplier instead of starting over.
  useEffect(() => {
    fetchCountries();

    const returningSupplierId = location.state?.supplierid;
    if (returningSupplierId) {
      setCreatedSupplierId(returningSupplierId);
      loadExistingSupplier(returningSupplierId);
    } else {
      generateSupplierCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">Supplier Master</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(-1)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
              isDark
                ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-100"
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </button>

          {createdSupplierId && (
            <div
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-emerald-900/40 text-emerald-300"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              <Building2 size={16} />
              Supplier ID: {createdSupplierId}
            </div>
          )}
        </div>

        {/* Main Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center gap-3 ${
              isDark ? "border-gray-700/50 bg-slate-900/40" : "border-gray-200 bg-gray-50/80"
            }`}
          >
            <div
              className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
            >
              <Building2 size={20} className="text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Supplier Details</h2>
              <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Fill in the supplier information below
              </p>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              {/* LEFT COLUMN */}
              <div className="space-y-5">
                {/* Supplier Name */}
                <div>
                  <label className={labelClass}>
                    Supplier Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <User
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      className={`${inputClass} pl-10`}
                      value={supplierName}
                      onChange={(e) => setSupplierName(e.target.value)}
                      placeholder="Enter supplier name"
                    />
                  </div>
                </div>

                {/* Supplier Code */}
                <div>
                  <label className={labelClass}>
                    Supplier Code
                    <span
                      className={`ml-2 text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase border ${
                        isDark
                          ? "bg-emerald-900/30 text-emerald-400 border-emerald-500/30"
                          : "bg-emerald-100 text-emerald-700 border-emerald-200"
                      }`}
                    >
                      Auto
                    </span>
                  </label>
                  <div className="relative">
                    <Hash
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      disabled
                      value={supplierCode}
                      className={`${inputClass} pl-10 opacity-80 cursor-not-allowed`}
                    />
                  </div>
                </div>

                {/* Supplier Type */}
                <div>
                  <label className={labelClass}>
                    Supplier Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={supplierType}
                    onChange={(e) => setSupplierType(e.target.value)}
                  >
                    <option value="">Select Type</option>
                    <option value="A">Vendor Approved</option>
                    <option value="B">Vendor Non Approved</option>
                  </select>
                </div>

                {/* Address 1 */}
                <div>
                  <label className={labelClass}>
                    Address 1 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <MapPin
                      size={16}
                      className="absolute left-3 top-3 text-gray-400"
                    />
                    <input
                      type="text"
                      className={`${inputClass} pl-10`}
                      value={address1}
                      onChange={(e) => setAddress1(e.target.value)}
                      placeholder="Street address"
                    />
                  </div>
                </div>

                {/* Address 2 */}
                <div>
                  <label className={labelClass}>Address 2</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={address2}
                    onChange={(e) => setAddress2(e.target.value)}
                    placeholder="Apartment, suite, etc."
                  />
                </div>

                {/* Country */}
                <div>
                  <label className={labelClass}>
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={selectedCountry}
                    onChange={(e) => {
                      const cid = e.target.value;
                      setSelectedCountry(cid);
                      setSelectedState("");
                      setSelectedDistrict("");
                      setSelectedCity("");
                      setStates([]);
                      setDistricts([]);
                      setCities([]);
                      fetchStates(cid);
                    }}
                  >
                    <option value="">Select Country</option>
                    {countries.map((c) => (
                      <option key={c.fld_countryid} value={c.fld_countryid}>
                        {c.fld_countryname}
                      </option>
                    ))}
                  </select>
                </div>

                {/* State */}
                <div>
                  <label className={labelClass}>
                    State <span className="text-red-500">*</span>
                  </label>
                  <select
                    className={inputClass}
                    value={selectedState}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedState(val);
                      setDivStateVisible(val === "other");
                      setSelectedDistrict("");
                      setSelectedCity("");
                      setDistricts([]);
                      setCities([]);
                      if (val !== "other" && val !== "") fetchDistricts(val);
                    }}
                  >
                    <option value="">Select State</option>
                    {states.map((s) => (
                      <option key={s.fld_stateid} value={s.fld_stateid}>
                        {s.fld_statename}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divStateVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new state"
                      value={stateText}
                      onChange={(e) => setStateText(e.target.value)}
                    />
                  )}
                </div>

                {/* District */}
                <div>
                  <label className={labelClass}>District</label>
                  <select
                    className={inputClass}
                    value={selectedDistrict}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedDistrict(val);
                      setDivDistrictVisible(val === "other");
                      setSelectedCity("");
                      setCities([]);
                      if (val !== "other" && val !== "") fetchCities(val);
                    }}
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.fld_districtid} value={d.fld_districtid}>
                        {d.fld_districtname}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divDistrictVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new district"
                      value={districtText}
                      onChange={(e) => setDistrictText(e.target.value)}
                    />
                  )}
                </div>

                {/* City */}
                <div>
                  <label className={labelClass}>City</label>
                  <select
                    className={inputClass}
                    value={selectedCity}
                    onChange={(e) => {
                      const val = e.target.value;
                      setSelectedCity(val);
                      setDivCityVisible(val === "other");
                    }}
                  >
                    <option value="">Select City</option>
                    {cities.map((c) => (
                      <option key={c.fld_cityid} value={c.fld_cityid}>
                        {c.fld_cityname}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                  {divCityVisible && (
                    <input
                      type="text"
                      className={`${inputClass} mt-2`}
                      placeholder="Enter new city"
                      value={cityText}
                      onChange={(e) => setCityText(e.target.value)}
                    />
                  )}
                </div>

                {/* Zipcode */}
                <div>
                  <label className={labelClass}>
                    Zipcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    className={inputClass}
                    value={zipcode}
                    onChange={(e) =>
                      // FIX: field accepted alphabets and unlimited length.
                      // Strip non-digits as the user types and cap at 6.
                      setZipcode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    placeholder="6-digit zip code"
                  />
                </div>
              </div>

              {/* RIGHT COLUMN */}
              <div className="space-y-5">
                {/* Email */}
                <div>
                  <label className={labelClass}>Email</label>
                  <div className="relative">
                    <Mail
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="email"
                      className={`${inputClass} pl-10`}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label className={labelClass}>Phone</label>
                  <div className="relative">
                    <Phone
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      className={`${inputClass} pl-10`}
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Landline number"
                    />
                  </div>
                </div>

                              {/* Mobile */}
                <div>
                  <label className={labelClass}>
                    Mobile <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Phone
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      className={`${inputClass} pl-10`}
                      value={mobile}
                      onChange={(e) =>
                        // FIX: field accepted alphabets and unlimited length.
                        // Strip non-digits as the user types and cap at 10.
                        setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))
                      }
                      placeholder="10-digit mobile number"
                    />
                  </div>
                </div>

                {/* TIN */}
                <div>
                  <label className={labelClass}>
                    TIN No <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={tin}
                    onChange={(e) => setTIN(e.target.value)}
                    placeholder="Tax Identification Number"
                  />
                </div>

                {/* GST */}
                <div>
                  <label className={labelClass}>GST No</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={gst}
                    onChange={(e) => setGST(e.target.value)}
                    placeholder="GST Number"
                  />
                </div>

                {/* PAN */}
                <div>
                  <label className={labelClass}>PAN No</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={pan}
                    onChange={(e) => setPAN(e.target.value)}
                    placeholder="PAN Number"
                  />
                </div>

                {/* Opening Balance */}
                <div>
                  <label className={labelClass}>Opening Balance</label>
                  <div className="relative">
                    <CreditCard
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <input
                      type="text"
                      className={`${inputClass} pl-10`}
                      value={openingBalance}
                      onChange={(e) => setOpeningBalance(e.target.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Ranking */}
                <div>
                  <label className={labelClass}>Ranking</label>
                  <div className="relative">
                    <Star
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <select
                      className={`${inputClass} pl-10`}
                      value={ranking}
                      onChange={(e) => setRanking(e.target.value)}
                    >
                      <option value="">Select Ranking</option>
                      <option value="1">Bad</option>
                      <option value="2">Ok</option>
                      <option value="3">Fair</option>
                      <option value="4">Good</option>
                      <option value="5">Excellent</option>
                    </select>
                  </div>
                </div>

                {/* Remarks */}
                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={3}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Additional notes..."
                  />
                </div>

                {/* Tally Header */}
                <div>
                  <label className={labelClass}>Tally Supplier Header</label>
                  <input
                    type="text"
                    className={inputClass}
                    value={tallyHeader}
                    onChange={(e) => setTallyHeader(e.target.value)}
                    placeholder="Tally header name"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap justify-end gap-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={resetForm}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg border transition ${
                  isDark
                    ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-100"
                }`}
              >
                <RotateCcw size={16} />
                Reset
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Save size={16} />
                Save Supplier
              </button>
            </div>
          </div>
        </div>

       
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
    </LayoutWrapper>
  );
};

export default SupplierMainMas;